import React, {
	ComponentType,
	CSSProperties,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { IconButton, Tooltip } from '@mui/material';
import {
	IconArrowLeft,
	IconArrowRight,
	IconMaximize,
	IconMinimize,
	IconPlayerPauseFilled,
	IconPlayerPlayFilled,
	IconRotate2,
	IconShare,
	IconVolume,
	IconVolume3,
} from '@tabler/icons-react';
import qs from 'qs';
import classNames from 'classnames';
import {
	filter,
	find,
	findIndex,
	get,
	includes,
	indexOf,
	isFunction,
	isUndefined,
	last,
	map,
	min,
	noop,
	sortBy,
} from 'lodash';
import { CustomControlButtonsType } from '../VideoPlayer.type';
import { useDispatch, useSelector } from 'react-redux';
import { setTrimFirstPlay, trackViewCountAction } from '../../../Pages/Flo/Flo.reducers';
import { floDuration } from '../../../Pages/Flo/Flo.selectors';
import { APP_WEBSITE_URL } from '../../../Common/Common.env';
import { ReactComponent as FloikLogo } from '../../../Common/images/floik-logo-grey.svg';
import {
	isSwiftPlay,
	preventDefaultForTouchEndEvent,
	sanitizeHtml,
	sanitizeHtmlLinks,
} from '../../../Common/Common.utils';
import MobileViewControlOptions from '../MobileViewControlOptions/MobileViewControlOptions';
import { hideDialogAction } from '../../VideoDialog/VideoDialog.reducer';
import { publishElementsDataType, stepsElementType } from '../../../Pages/Flo/Flo.types';
import { enablePoweredBYSelector } from '../../../Common/Common.selector';
import { CustomProgressBar } from '../CustomInVideoControls/CustomInVideoControls';
import {
	FLO_PAGE_ID_RESTART,
	FLO_PAGE_SHARE_FLO_TOOLTIP,
	FLO_PAGE_VIDEO_MAXIMIZE,
	FLO_PAGE_VIDEO_MINIMIZE,
	FLO_PAGE_VIDEO_MUTE,
	FLO_PAGE_VIDEO_NEXT,
	FLO_PAGE_VIDEO_PAUSE,
	FLO_PAGE_VIDEO_PLAY,
	FLO_PAGE_VIDEO_PREVIOUS,
	FLO_PAGE_VIDEO_UNMUTE,
} from '../../../Common/Common.constants';
import { ReactJSXElement } from '@emotion/react/types/jsx-namespace';
import { MAX_TABLET_VIEW_WIDTH } from '../../../Common/Common.params';
import ShareFloButton, { useShareFloClick } from '../../ShareFloButton/ShareFloButton';
import { setAutoPlay } from '../../../Pages/Flo/Flo.actions';
import { createPortal } from 'react-dom';
import styles from './InteractiveModeControls.module.css';

export type ControlItemType = {
	name: string;
	order: number;
	priority: number;
	width: number;
	tabViewWidth?: number;
	type?: string;
	icon?: ComponentType;
	label?: string;
	getWidth?(parentWidth: number): number;
	style?: CSSProperties;
	tooltipText?: string;
};

const controlsMap: { [key: string]: ControlItemType } = {
	flo_name: {
		name: 'flo_name',
		order: 1,
		priority: 1,
		getWidth: (parentWidth) => 0.33 * parentWidth,
		width: 32,
		type: 'text',
		label: 'flo_name',
	},
	prev_button: {
		name: 'prev_button',
		order: 2,
		priority: 2,
		width: 32,
		type: 'button',
		label: 'Prev',
		icon: IconArrowLeft,
		tooltipText: FLO_PAGE_VIDEO_PREVIOUS,
	},
	steps_info: {
		name: 'steps_info',
		order: 3,
		priority: 3,
		width: 75,
		type: 'text',
		label: '',
	},
	next_button: {
		name: 'next_button',
		order: 4,
		priority: 5,
		width: 32,
		type: 'button',
		icon: IconArrowRight,
		label: 'Next',
		style: { marginRight: 'auto' },
		tooltipText: FLO_PAGE_VIDEO_NEXT,
	},
	seek_bar: {
		name: 'seek_bar',
		order: 5,
		priority: 4,
		getWidth: (parentWidth) => {
			if (parentWidth < 320) {
				return 100;
			} else if (parentWidth < 450) {
				return 124;
			} else if (parentWidth < 660) {
				return 0.4 * parentWidth;
			}
			return 360;
		},
		width: 360,
	},
	pause_play_button: {
		name: 'pause_play_button',
		order: 6,
		priority: 6,
		width: 32,
		type: 'button',
	},
	powered_by_floik_text: {
		name: 'powered_by_floik_text',
		order: 7,
		priority: 11,
		width: 80,
		type: 'text',
	},
	restart_button: {
		name: 'restart_button',
		order: 8,
		priority: 9,
		width: 32,
		type: 'button',
		icon: IconRotate2,
		label: 'Restart',
		tooltipText: FLO_PAGE_ID_RESTART,
	},
	share: {
		name: 'share',
		order: 8,
		priority: 9,
		width: 32,
		type: 'button',
		icon: IconShare,
		label: 'Share Flo',
		tooltipText: FLO_PAGE_SHARE_FLO_TOOLTIP,
	},
	mute_button: {
		name: 'mute_button',
		order: 9,
		priority: 8,
		width: 32,
		type: 'button',
	},
	full_screen_button: {
		name: 'full_screen_button',
		order: 10,
		priority: 10,
		width: 32,
		type: 'button',
	},
};

const DEFAULT_CONTROLS = [
	get(controlsMap, 'prev_button'),
	get(controlsMap, 'restart_button'),
];

const InteractiveModeControls = ({
	autoPlay,
	nodeRef,
	onVolumeChange,
	volume,
	isFullScreen,
	currentHotspot,
	isTabView,
	showEndCta,
	goToStep,
	isAiVoice,
	hasAudioTrack,
	customProgressBarProps,
	onTogglePlay,
	playing,
	parentRef,
	activityPosition,
	width,
	resetPrevHotspot,
	resetLastSeekedTime,
	disabledControls,
	onArrowClickCb,
	onPoweredByClick,
	currentElementType,
	sliderIndex: sliderIndexProp = 0,
	setCurrentElementIndex,
	onRestart,
	showSeekbar,
	onPause,
	volumeContainerRef,
	floId,
	floIndex,
}: CustomControlButtonsType) => {
	const dispatch = useDispatch();
	// @ts-ignore
	const hideHeader = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails["hide-header"]`)
	);
	const maxProgressed = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.postProcessConfigs.trim.maxProgressed`)
	);
	const hasNotPlayed = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.postProcessConfigs.trim.hasNotPlayed`)
	);
	const [controlBarWidth, setControlBarWidth] = useState<number | string>(0);
	const floName = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails.heading`)
	);
	const [sliderIndex, setSliderIndex] = useState(-1);
	const ctaAndHotspotTimesList = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.ctaAndHotspotTimesList`)
	);
	const hotspotInfoWrapperRef =
		useRef<HTMLDivElement>() as React.RefObject<HTMLDivElement>;
	const stepsInfoWrapperRef = useRef<HTMLDivElement>() as React.RefObject<HTMLDivElement>;
	const hotspotTitleRef = useRef<HTMLDivElement>() as React.RefObject<HTMLDivElement>;
	const hotspotDescriptionRef =
		useRef<HTMLDivElement>() as React.RefObject<HTMLDivElement>;
	const skipInactivity = isSwiftPlay(floIndex);
	const rangeRef = useRef<HTMLDivElement>() as React.RefObject<HTMLDivElement>;
	const containerRef = useRef<HTMLDivElement>() as React.RefObject<HTMLDivElement>;
	const [rangeSiblingsWidth, setRangeSiblingsWidth] = useState(0);
	const [hotspotInfoOverflowing, setHotspotInfoOverflowing] = useState(false);
	const [controlsPosition, setControlsPosition] = useState<CSSProperties>({});
	const [showControls, setShowControls] = useState(false);

	const ctaAndHotspotTimes = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.ctaAndHotspotTimes`)
	);
	const currentTimeSeconds = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.postProcessConfigs.trim.currentTimeSeconds`, 0)
	);
	const duration = useSelector(floDuration(floIndex)) || 0;
	const isDialogVisible = useSelector((state) =>
		get(state, `videoDialog.${floIndex}.visible`)
	);
	const dialogId = useSelector((state) => get(state, `videoDialog.${floIndex}.id`));
	const [controlsList, setControlsList] = useState<ControlItemType[]>([]);
	const [controlsInOptionsList, setControlsInOptionsList] = useState<ControlItemType[]>(
		[]
	);

	const [marks, setMarks] = useState<Array<{ value: number }>>([]);
	const [sliderValue, setSliderValue] = useState(0);
	const floElements: {
		[key: string]: publishElementsDataType[];
	} = useSelector((state) => get(state, `floPage.flos.${floIndex}.editorsElements`, {}));
	const showPoweredBy = useSelector(enablePoweredBYSelector);
	const [tooltipParent, setTooltipParent] = useState<HTMLElement | null>(null);
	const { onClick: onShareClick, shareAllowed } = useShareFloClick({ floId });

	const [seekBarIndex, setSeekBarIndex] = useState(-1);
	const parentWidth = parentRef?.current?.offsetWidth || 0;
	const parentHeight = parentRef?.current?.offsetHeight || 0;

	useEffect(() => {
		setSliderIndex(sliderIndexProp);
	}, [
		sliderIndexProp,
		setSliderIndex,
		ctaAndHotspotTimesList,
		sliderValue,
		isDialogVisible,
		dialogId,
		floElements,
		currentHotspot,
		ctaAndHotspotTimes,
		nodeRef,
		duration,
	]);

	const showFloName = (isFullScreen || hideHeader) && !isTabView && false;
	const isDescriptionPresent = currentHotspot.hotspotType
		? includes(currentHotspot.hotspotType, 'desc')
		: !!get(currentHotspot, 'values.description');
	const isTitlePresent = currentHotspot.hotspotType
		? includes(currentHotspot.hotspotType, 'title')
		: !!(
				get(currentHotspot, 'values.title') || get(currentHotspot, 'values.name')
		  )?.trim();

	useEffect(() => {
		const target = hotspotInfoWrapperRef?.current;
		if (target) {
			let child = null;
			if (isTitlePresent && !isDescriptionPresent) {
				child = hotspotTitleRef?.current;
			} else if (!isTitlePresent && isDescriptionPresent) {
				child = hotspotDescriptionRef?.current;
			}
			if (child && (child?.clientHeight || 0) >= target.offsetHeight) {
				setHotspotInfoOverflowing(true);
			} else {
				setHotspotInfoOverflowing(false);
			}
		}
	}, [
		setHotspotInfoOverflowing,
		hotspotInfoWrapperRef,
		isTitlePresent,
		hotspotTitleRef?.current?.clientHeight,
		hotspotDescriptionRef?.current?.clientHeight,
		isDescriptionPresent,
		currentHotspot.id,
	]);

	const isVideoLessThan650 = (width || 0) < MAX_TABLET_VIEW_WIDTH;

	const getControlsList = useCallback(
		(availableWidth: number, isReducedWidth?: boolean) => {
			let newControlsInOptionsList: ControlItemType[] = [];
			const notShowingHotspot = !currentHotspot.id;
			let newControlsList: ControlItemType[] = [
				...DEFAULT_CONTROLS,
				{
					...controlsMap.steps_info,
					style: notShowingHotspot || !isVideoLessThan650 ? {} : { marginRight: 'auto' },
					width:
						stepsInfoWrapperRef?.current?.offsetWidth || controlsMap.steps_info.width,
				},
				{
					...controlsMap.full_screen_button,
					icon: isFullScreen ? IconMinimize : IconMaximize,
					label: isFullScreen ? 'Minimize' : 'Maximize',
					tooltipText: isFullScreen ? FLO_PAGE_VIDEO_MINIMIZE : FLO_PAGE_VIDEO_MAXIMIZE,
				},
			];
			if (showFloName) {
				const floNameWidth = controlsMap.flo_name?.getWidth
					? controlsMap.flo_name?.getWidth(availableWidth || 0)
					: controlsMap.flo_name.width;
				newControlsList.push({
					...controlsMap.flo_name,
					width: floNameWidth,
				});
			}
			if (notShowingHotspot || !isVideoLessThan650) {
				newControlsList.push(get(controlsMap, 'next_button'));
			}
			if (!skipInactivity) {
				let seekBarWidth = 0;
				if (showFloName) {
					seekBarWidth = availableWidth - (260 + 0.33 * availableWidth);
				} else {
					seekBarWidth = controlsMap.seek_bar?.getWidth
						? controlsMap.seek_bar?.getWidth(availableWidth || 0)
						: controlsMap.seek_bar.width;
				}
				newControlsList = newControlsList.concat([
					{
						...controlsMap.pause_play_button,
						icon: playing ? IconPlayerPauseFilled : IconPlayerPlayFilled,
						label: playing ? 'Pause' : 'Play',
						tooltipText: playing ? FLO_PAGE_VIDEO_PAUSE : FLO_PAGE_VIDEO_PLAY,
					},
					{
						...controlsMap.seek_bar,
						width: seekBarWidth,
					},
				]);
			}
			if (shareAllowed) {
				newControlsList.push(controlsMap.share);
			}
			if (hasAudioTrack) {
				newControlsList.push({
					...controlsMap.mute_button,
					icon: volume === 0 ? IconVolume3 : IconVolume,
					label: volume === 0 ? 'Unmute' : 'Mute',
					tooltipText: volume === 0 ? FLO_PAGE_VIDEO_UNMUTE : FLO_PAGE_VIDEO_MUTE,
				});
			}
			if (showPoweredBy) {
				newControlsList.push(get(controlsMap, 'powered_by_floik_text'));
			}
			newControlsList = sortBy(newControlsList, ['priority']);
			let totalWidth = 0;
			availableWidth =
				!notShowingHotspot && isVideoLessThan650
					? availableWidth > 250
						? 176
						: availableWidth - availableWidth * 0.3
					: availableWidth -
					  (availableWidth > 320 ? 48 : 20) / (isReducedWidth ? 2 : 1) -
					  15; // 176 bcoz we want to move the other actions to options
			let overflowIndex = findIndex(newControlsList, (item, index) => {
				totalWidth += item.width;
				if (index < newControlsList.length - 1) {
					return totalWidth > (availableWidth || 0) - 36; // -36 bcoz if it is not the last item consider options icon width;
				}
				return totalWidth > (availableWidth || 0);
			});
			if (overflowIndex === -1) {
				newControlsInOptionsList = [];
			} else {
				newControlsInOptionsList = filter(
					newControlsList.slice(overflowIndex),
					(controlItem) =>
						get(controlItem, 'type') === 'button' &&
						get(controlItem, 'name') !== 'next_button'
				);
				if (
					newControlsInOptionsList.length === 1 &&
					newControlsInOptionsList[0].width <= 36
				) {
					newControlsList = newControlsList.slice(0, overflowIndex + 1);
					newControlsInOptionsList = [];
				} else {
					newControlsList = newControlsList.slice(0, overflowIndex);
				}
			}
			return {
				controlsList: newControlsList,
				controlsInOptionsList: newControlsInOptionsList,
				totalWidth,
			};
		},
		[
			isTabView,
			currentHotspot,
			showPoweredBy,
			skipInactivity,
			playing,
			isFullScreen,
			volume,
			parentWidth,
			hideHeader,
			showFloName,
			stepsInfoWrapperRef?.current?.offsetWidth,
			width,
			shareAllowed,
		]
	);

	useEffect(() => {
		let values = getControlsList(parentWidth);
		const notShowingHotspot = !currentHotspot.id;
		if (parentHeight && !notShowingHotspot) {
			const childRect = {
				x: (parentWidth - values.totalWidth) / 2,
				y: parentHeight - 64,
				width: values.totalWidth,
			};
			const parentRect = parentRef?.current?.getBoundingClientRect() || {
				x: 0,
				y: 0,
				height: 0,
				width: 0,
			};
			const activityRect = activityPosition || { left: 0, top: 0, right: 0 };
			const controlBarLeft = childRect.x;
			const horizontalOverlap =
				activityRect.left > controlBarLeft - 40 &&
				activityRect.left < controlBarLeft + childRect.width; // 40 is for the hotspot
			const verticalOverlap =
				activityRect.top > parentRect.height - 90 &&
				(parentRect.height > 105 || activityRect.top + 24 > parentRect.height / 2); // 90 is controlbarheight(approx 50) + hotspot height, second cond is when video height is less than 105 case only

			if (
				(verticalOverlap && horizontalOverlap) ||
				(verticalOverlap && isVideoLessThan650)
			) {
				let controlsStyle: CSSProperties = {};
				const halfOfParentWidth = parentRect.width / 2;
				const position = isVideoLessThan650 ? 0 : 24;
				if (activityRect.left + 24 <= halfOfParentWidth) {
					const widthInCenterWithActivityRect =
						parentWidth - (activityRect.left + 48) * 2 - 10;
					if (widthInCenterWithActivityRect < halfOfParentWidth) {
						controlsStyle = {
							width:
								activityRect.left + 48 > halfOfParentWidth
									? halfOfParentWidth - 30
									: halfOfParentWidth,
							right: position,
							position: 'absolute',
						};
					} else {
						controlsStyle = {
							width: widthInCenterWithActivityRect,
						};
					}
					if (isVideoLessThan650) {
						controlsStyle = {
							...controlsStyle,
							borderRadius:
								widthInCenterWithActivityRect < halfOfParentWidth
									? '8px 0 8px 8px'
									: '8px',
						};
					}
				} else {
					const activityRight = parentWidth - activityRect.left;
					const widthInCenterWithActivityRect = parentWidth - activityRight * 2 - 10;
					if (widthInCenterWithActivityRect < halfOfParentWidth) {
						controlsStyle = {
							width:
								activityRect.left < halfOfParentWidth
									? halfOfParentWidth - 30
									: halfOfParentWidth,
							left: position,
							position: 'absolute',
						};
					} else {
						controlsStyle = {
							width: widthInCenterWithActivityRect,
						};
					}
					if (isVideoLessThan650) {
						controlsStyle = {
							...controlsStyle,
							borderRadius:
								widthInCenterWithActivityRect < halfOfParentWidth
									? '0px 8px 8px 8px'
									: '8px',
						};
					}
				}
				setControlsPosition(controlsStyle);
				values = getControlsList(controlsStyle.width as number, true);
			} else {
				setControlsPosition({
					width: isVideoLessThan650 ? '100%' : parentWidth,
				});
			}
		} else {
			setControlsPosition({
				width: '100%',
			});
		}
		const newSeekBarIndex = values.controlsList.findIndex(
			(item) => item.name === 'seek_bar'
		);
		setSeekBarIndex(newSeekBarIndex);
		setControlsList(values.controlsList);
		setControlsInOptionsList(values.controlsInOptionsList);
	}, [
		getControlsList,
		currentHotspot,
		parentHeight,
		parentRef,
		activityPosition,
		setControlsPosition,
		setSeekBarIndex,
		setControlsList,
		setControlsInOptionsList,
		parentWidth,
	]);

	const toggleFullScreen = useCallback(
		(event?: React.MouseEvent | React.TouchEvent) => {
			preventDefaultForTouchEndEvent(event);
			const videoContainer = document.querySelector('.videoPlayerContainer');
			if (!isFullScreen) {
				// @ts-ignore
				if (videoContainer.requestFullscreen) {
					// @ts-ignore
					videoContainer.requestFullscreen();
					// @ts-ignore
				} else if (videoContainer.mozRequestFullScreen) {
					// @ts-ignore
					videoContainer.mozRequestFullScreen();
					// @ts-ignore
				} else if (videoContainer.webkitRequestFullscreen) {
					// @ts-ignore
					videoContainer.webkitRequestFullscreen();
					// @ts-ignore
				} else if (videoContainer.msRequestFullscreen) {
					// @ts-ignore
					videoContainer.msRequestFullscreen();
				}
			}
			if (isFullScreen) {
				// @ts-ignore
				if (document.exitFullscreen) {
					// @ts-ignore
					document.exitFullscreen();
					// @ts-ignore
				} else if (document.mozCancelFullScreen) {
					// @ts-ignore
					document.mozCancelFullScreen();
					// @ts-ignore
				} else if (document.webkitExitFullscreen) {
					// @ts-ignore
					document.webkitExitFullscreen();
					// @ts-ignore
				} else if (document.msExitFullscreen) {
					// @ts-ignore
					document.msExitFullscreen();
				}
			}
		},
		[isFullScreen]
	);

	useEffect(() => {
		setMarks(
			map(ctaAndHotspotTimesList, (item: stepsElementType) => {
				if (item.time > duration && item.isEndCta) {
					return {
						value: duration - 0.1,
					};
				}
				return {
					value: item.time,
				};
			})
		);
	}, [ctaAndHotspotTimesList, setMarks]);

	useEffect(() => {
		// @ts-ignore
		setTooltipParent(document.querySelectorAll('[class*=fullScreenContainer]')?.[0]);
	}, [isFullScreen, setTooltipParent]);

	useEffect(() => {
		const value = find(
			ctaAndHotspotTimes,
			(item: number) =>
				currentTimeSeconds >= item - 0.1 && currentTimeSeconds < item + 0.35
		);
		if (
			currentTimeSeconds === 0 &&
			sliderIndex === -1 &&
			nodeRef?.current &&
			ctaAndHotspotTimesList
		) {
			// @ts-ignore
			const firstElement: stepsElementType = ctaAndHotspotTimesList?.[0];
			if (skipInactivity && firstElement?.time > 0.5) {
				if (firstElement.isEndCta) {
					const adjustedTime = Math.min(firstElement.time, duration);
					setSliderValue(adjustedTime);
					nodeRef?.current?.currentTime(adjustedTime);
					showEndCta?.();
				} else {
					dispatch(setTrimFirstPlay({ data: false, floId, floIndex }));
					setSliderValue(firstElement.time);
					nodeRef?.current?.currentTime(firstElement.time);
				}
			} else {
				setSliderValue(0);
			}
		}
		if (!isUndefined(value) && value >= 0 && sliderValue !== value) {
			setSliderValue(value);
		} else if (currentTimeSeconds > duration - 0.35) {
			setSliderValue(duration);
		}
	}, [
		currentTimeSeconds,
		hasNotPlayed,
		duration,
		ctaAndHotspotTimes,
		ctaAndHotspotTimesList,
		setSliderValue,
		showEndCta,
		floId,
	]);

	const lastTime = last(ctaAndHotspotTimes) || duration;

	const checkAndCloseCta = useCallback(
		(newValueIndex: number) => {
			// @ts-ignore
			if (ctaAndHotspotTimesList?.[newValueIndex]?.type !== 'cta' && isDialogVisible) {
				dispatch(
					hideDialogAction({
						data: '',
						floId,
						floIndex,
					})
				);
			}
		},
		[ctaAndHotspotTimesList, isDialogVisible, floId]
	);

	const onSliderChange = useCallback(
		(value: number) => {
			const newValueIndex = indexOf(ctaAndHotspotTimes, value);
			checkAndCloseCta(newValueIndex);

			// @ts-ignore
			if ((maxProgressed || 0) >= value) {
				if (value === 0) {
					dispatch(setTrimFirstPlay({ data: true, floId, floIndex }));
				} else if (
					// @ts-ignore
					ctaAndHotspotTimesList?.[ctaAndHotspotTimesList.length - 1]?.isEndCta &&
					// @ts-ignore
					value > (duration || 0) - 0.35 &&
					dialogId !== 'end' &&
					nodeRef?.current?.currentTime() !== 0
				) {
					showEndCta?.(true);
				}
			}
			setSliderValue(value);
		},
		[
			nodeRef,
			maxProgressed,
			ctaAndHotspotTimesList,
			showEndCta,
			ctaAndHotspotTimes,
			// setSliderIndex,
			setSliderValue,
			floId,
		]
	);

	const valueLabelFormat = useCallback(
		(value: number, index: number) => {
			return indexOf(ctaAndHotspotTimes, value) + 1;
		},
		[ctaAndHotspotTimes]
	);

	const onArrowClick = useCallback(
		(curIndex: number, next?: boolean) => {
			dispatch(setAutoPlay({ data: false, floId, floIndex }));
			onArrowClickCb?.(curIndex, next);
			const newIndex = curIndex + (next ? 1 : -1);
			checkAndCloseCta(newIndex);
			let newSliderValue = ctaAndHotspotTimes?.[newIndex] || 0;
			if (get(ctaAndHotspotTimesList, `[${newIndex}].isEndCta`)) {
				nodeRef?.current?.currentTime(min([newSliderValue, duration]) || 0);
			} else if (
				// @ts-ignore
				get(ctaAndHotspotTimesList, `[${newIndex}].isEndCta`) &&
				newSliderValue > (duration || 0) - 0.35 &&
				dialogId !== 'end' &&
				nodeRef?.current?.currentTime() !== 0
			) {
				nodeRef?.current?.currentTime(min([newSliderValue, duration]) || 0);
				showEndCta?.(true);
			} else {
				// @ts-ignore
				const curElementId = ctaAndHotspotTimesList?.[curIndex]?.id;
				const resumePlay = goToStep?.(curElementId, next, true);
				if (resumePlay) {
					nodeRef?.current?.play();
				}
			}
			if (newIndex === 0) {
				if (!next && nodeRef?.current) {
					nodeRef?.current?.pause();
				}
				if (get(ctaAndHotspotTimesList, `${newIndex}.time`) === 0) {
					dispatch(setTrimFirstPlay({ data: true, floId, floIndex }));
				}
			}
			setSliderValue(newSliderValue);
		},
		[
			goToStep,
			nodeRef,
			dialogId,
			ctaAndHotspotTimesList,
			checkAndCloseCta,
			ctaAndHotspotTimes,
			setSliderValue,
			showEndCta,
			floId,
		]
	);

	const onRestartClick = useCallback(
		(event?: React.MouseEvent | React.TouchEvent) => {
			preventDefaultForTouchEndEvent(event);
			const paused = nodeRef?.current?.paused();
			setCurrentElementIndex(-1);
			resetLastSeekedTime?.();
			if (!paused) {
				nodeRef?.current?.pause();
			}
			nodeRef?.current?.currentTime(0);
			setSliderValue(0);
			if (isDialogVisible && dialogId !== 'start') {
				dispatch(
					hideDialogAction({
						data: dialogId,
						floId,
						floIndex,
					})
				);
			}
			dispatch(setTrimFirstPlay({ data: true, floId, floIndex }));
			dispatch(trackViewCountAction({ restart: true, floId, floIndex }));
			resetPrevHotspot?.();
			onRestart();
		},
		[
			nodeRef,
			onRestart,
			setCurrentElementIndex,
			setSliderValue,
			isDialogVisible,
			dialogId,
			resetPrevHotspot,
			resetLastSeekedTime,
			floId,
		]
	);

	const onVolumeIconClick = useCallback(
		(event?: React.MouseEvent | React.TouchEvent) => {
			preventDefaultForTouchEndEvent(event);
			if (isFunction(onVolumeChange)) {
				onVolumeChange({
					isMuted: volume !== 0,
					volume: volume === 0 ? 1 : 0,
				});
			}
		},
		[onVolumeChange, volume]
	);

	useEffect(() => {
		if ((isFullScreen || hideHeader) && !isTabView) {
			const containerWidth = containerRef.current?.offsetWidth || 0;
			const rangeWidth = rangeRef?.current?.offsetWidth || 0;
			setRangeSiblingsWidth((containerWidth - rangeWidth) / 2);
		}
	}, [
		rangeRef?.current?.offsetWidth,
		containerRef?.current?.offsetWidth,
		isFullScreen,
		hideHeader,
		isTabView,
		setRangeSiblingsWidth,
	]);

	const onOptionClick = useCallback(
		(item: ControlItemType, event: React.MouseEvent | React.TouchEvent) => {
			preventDefaultForTouchEndEvent(event);
			const { name } = item;
			switch (name) {
				case 'restart_button': {
					onRestartClick();
					break;
				}
				case 'mute_button': {
					onVolumeIconClick();
					break;
				}
				case 'pause_play_button': {
					onTogglePlay(event);
					break;
				}
				case 'full_screen_button': {
					toggleFullScreen();
					break;
				}
				case 'share': {
					onPause?.();
					onShareClick();
					break;
				}
				default:
					break;
			}
		},
		[
			onRestartClick,
			onVolumeIconClick,
			onTogglePlay,
			toggleFullScreen,
			onShareClick,
			onPause,
		]
	);

	const onClickProgressBar = useCallback(
		(event: React.MouseEvent) => {
			dispatch(setAutoPlay({ data: false, floId, floIndex }));
			customProgressBarProps?.onClickProgressBar(event, (val: number) => {
				onSliderChange(val);
			});
		},
		[customProgressBarProps?.onClickProgressBar, onSliderChange, floId]
	);

	useEffect(() => {
		if (!playing && !dialogId) {
			if (currentElementType === 'image') {
				setShowControls(showSeekbar || false);
			} else {
				setShowControls(true);
			}
		} else {
			setShowControls(false);
		}
	}, [playing, dialogId, showSeekbar, currentElementType, setShowControls]);

	const onPrevClick = useCallback(
		(e?: React.MouseEvent | React.TouchEvent) => {
			preventDefaultForTouchEndEvent(e);
			if (sliderIndex === 0 || isDialogVisible) return;
			onArrowClick(sliderIndex || 0);
		},
		[sliderIndex, isDialogVisible, onArrowClick]
	);

	const onNextClick = useCallback(
		(e?: React.MouseEvent | React.TouchEvent) => {
			preventDefaultForTouchEndEvent(e);
			if (sliderIndex === marks.length - 1 || isDialogVisible) return;
			onArrowClick(sliderIndex, true);
		},
		[sliderIndex, isDialogVisible, onArrowClick, marks]
	);

	const handleKeyUp = useCallback(
		(e: KeyboardEvent) => {
			if (currentElementType === 'image') return;
			if (e.key === 'ArrowRight') {
				onNextClick();
			} else if (e.key === 'ArrowLeft') {
				onPrevClick();
			}
		},
		[onPrevClick, onNextClick, currentElementType]
	);

	useEffect(() => {
		window.addEventListener('keyup', handleKeyUp);
		return () => {
			window.removeEventListener('keyup', handleKeyUp);
		};
	}, [handleKeyUp]);

	const tooltipPosition = 'top';
	const showInnerControls =
		!autoPlay ||
		(autoPlay &&
			(width || 0) < MAX_TABLET_VIEW_WIDTH &&
			(isTitlePresent || isDescriptionPresent));

	return (
		<div className={classNames(styles.wrapper, { [styles.showInFront]: showSeekbar })}>
			{volumeContainerRef?.current &&
				createPortal(
					<div className={styles.floatingMuteIcon} onClick={onVolumeIconClick}>
						{volume === 0 ? <IconVolume3 size={20} /> : <IconVolume size={20} />}
					</div>,
					volumeContainerRef.current
				)}
			<div
				className={classNames(styles.container, {
					[styles.withHotspot]: currentHotspot.id,
					// [styles.controlsOnTop]: controlsPosition === 'top',
					[styles.container320]: (parentWidth || 0) <= 320,
					[styles.show]: showControls && showInnerControls,
					[styles.autoPlay]: !showInnerControls,
					[styles.skipInactivity]: skipInactivity,
					[styles.noBg]: !showInnerControls,
				})}
				style={{
					...controlsPosition,
					width:
						isVideoLessThan650 &&
						currentHotspot.id &&
						((controlsPosition?.width || 0) < parentWidth ||
							controlsPosition?.width === '100%')
							? controlsPosition.width
							: 'auto',
					maxWidth: controlsPosition.width || 'auto',
				}}
				ref={containerRef}
				onTouchEnd={(e: React.TouchEvent) => {
					e.stopPropagation();
					e.preventDefault();
				}}
			>
				{showInnerControls &&
					controlsList.map((controlItem) => {
						const {
							icon: Icon,
							name,
							order,
							style,
							width,
							tabViewWidth,
							tooltipText,
						} = controlItem;
						let Comp: ReactJSXElement = <></>;

						const tooltipTitle = showControls ? tooltipText : '';
						if (name === 'seek_bar') {
							return <></>;
						}
						switch (name) {
							case 'flo_name': {
								Comp = (
									<div
										className={styles.welcome}
										// style={{ width: `${rangeSiblingsWidth}px` }}
									>
										{floName}
									</div>
								);
								break;
							}
							case 'prev_button': {
								Comp = (
									<Tooltip
										PopperProps={{
											container: tooltipParent,
										}}
										classes={{
											popper: styles.tooltip,
										}}
										arrow
										title={tooltipTitle}
										placement={tooltipPosition}
									>
										{/*@ts-ignore*/}
										<IconButton
											className={styles.arrowButton}
											onClick={onPrevClick}
											onTouchEnd={onPrevClick}
										>
											<div className={styles.arrowIconWrapper}>
												{/*@ts-ignore*/}
												<Icon
													size={17}
													className={classNames(styles.arrowIcon, {
														[styles.active]: sliderIndex !== 0 && !isDialogVisible,
													})}
												/>
											</div>
										</IconButton>
									</Tooltip>
								);
								break;
							}
							case 'steps_info': {
								Comp = (
									<div className={styles.countContainer} ref={stepsInfoWrapperRef}>
										{sliderIndex + 1}/{marks.length}
									</div>
								);
								break;
							}
							case 'next_button': {
								Comp = (
									<Tooltip
										PopperProps={{
											container: tooltipParent,
										}}
										classes={{
											popper: styles.tooltip,
										}}
										arrow
										title={tooltipTitle}
										placement={tooltipPosition}
									>
										<IconButton
											className={classNames(styles.arrowButton, styles.rightArrowButton, {
												[styles.disabled]: get(disabledControls, 'next_button', false),
											})}
											onClick={
												get(disabledControls, 'next_button', false) ? noop : onNextClick
											}
											onTouchEnd={
												get(disabledControls, 'next_button', false) ? noop : onNextClick
											}
											disabled={get(disabledControls, 'next_button', false)}
										>
											<div className={styles.arrowIconWrapper}>
												{/*@ts-ignore*/}
												<Icon
													size={17}
													className={classNames(styles.arrowIcon, {
														[styles.active]:
															sliderIndex !== marks.length - 1 && !isDialogVisible,
													})}
												/>
											</div>
										</IconButton>
									</Tooltip>
								);
								break;
							}
							case 'seek_bar': {
								Comp = (
									<div
										className={styles.progressBarWrapper}
										style={{ width: `${isTabView ? tabViewWidth || width : width}px` }}
									>
										<CustomProgressBar
											{...customProgressBarProps}
											onClickProgressBar={onClickProgressBar}
											interactiveMode={true}
										/>
									</div>
								);
								break;
							}
							case 'pause_play_button': {
								// @ts-ignore
								Comp = (
									<Tooltip
										PopperProps={{
											container: tooltipParent,
										}}
										classes={{
											popper: styles.tooltip,
										}}
										arrow
										title={tooltipTitle}
										placement={tooltipPosition}
									>
										{/*@ts-ignore*/}
										<IconButton
											className={styles.button}
											onClick={onTogglePlay}
											onTouchEnd={(event: React.TouchEvent) => {
												event?.preventDefault();
												onTogglePlay(event);
											}}
										>
											{/*@ts-ignore*/}
											<Icon size={17} className={styles.icon} />
										</IconButton>
									</Tooltip>
								);
								break;
							}
							case 'powered_by_floik_text': {
								Comp = (
									<div className={styles.poweredBy}>
										<a
											href={APP_WEBSITE_URL}
											target="_blank"
											className={styles.link}
											onClick={onPoweredByClick}
										>
											<span className={styles.poweredBytext}> Powered by </span>
											<FloikLogo className={styles.floikSVGIcon} />
										</a>
									</div>
								);
								break;
							}
							case 'restart_button': {
								Comp = (
									<Tooltip
										PopperProps={{
											container: tooltipParent,
										}}
										classes={{
											popper: styles.tooltip,
										}}
										arrow
										title={tooltipTitle}
										placement={tooltipPosition}
									>
										<IconButton
											className={styles.button}
											onClick={onRestartClick}
											onTouchEnd={onRestartClick}
										>
											{/*@ts-ignore*/}
											<Icon size={16} />
										</IconButton>
									</Tooltip>
								);
								break;
							}
							case 'share': {
								Comp = (
									<ShareFloButton
										floIndex={floIndex}
										floId={floId}
										iconProps={{
											size: 16,
										}}
										classes={{
											shareButton: styles.button,
										}}
										tooltipProps={{
											PopperProps: {
												container: tooltipParent,
											},
											placement: tooltipPosition,
										}}
										onShareClick={onPause}
									/>
								);
								break;
							}
							case 'mute_button': {
								Comp = (
									<Tooltip
										PopperProps={{
											container: tooltipParent,
										}}
										classes={{
											popper: styles.tooltip,
										}}
										arrow
										title={tooltipTitle}
										placement={tooltipPosition}
									>
										<IconButton
											className={styles.button}
											onClick={onVolumeIconClick}
											onTouchEnd={onVolumeIconClick}
										>
											{/*@ts-ignore*/}
											<Icon size={16} />
										</IconButton>
									</Tooltip>
								);
								break;
							}
							case 'full_screen_button': {
								Comp = (
									<Tooltip
										PopperProps={{
											container: tooltipParent,
										}}
										classes={{
											popper: styles.tooltip,
										}}
										arrow
										title={tooltipTitle}
										placement={tooltipPosition}
									>
										<IconButton
											className={styles.button}
											onClick={toggleFullScreen}
											onTouchEnd={toggleFullScreen}
										>
											{/*@ts-ignore*/}
											<Icon size={16} />
										</IconButton>
									</Tooltip>
								);
								break;
							}
						}
						return (
							<div
								className={classNames(styles.controlItemWrapper, {
									[styles.floName]: name === 'flo_name',
								})}
								style={{ ...(style || {}), order: order }}
								key={name}
							>
								{Comp}
							</div>
						);
					})}
				{showInnerControls && (
					<div
						className={classNames(styles.controlItemWrapper, {
							[styles.hideSeekBar]: seekBarIndex === -1,
							[styles.disabled]: currentHotspot.id,
						})}
						style={{
							...(controlsMap.seek_bar.style || {}),
							order: controlsMap.seek_bar.order,
						}}
						key={'seek_bar'}
					>
						<div
							className={styles.progressBarWrapper}
							style={{ width: `${controlsList[seekBarIndex]?.width}px` }}
						>
							<CustomProgressBar
								{...customProgressBarProps}
								onClickProgressBar={currentHotspot.id ? noop : onClickProgressBar}
								interactiveMode={true}
								classes={{
									progressContainer: classNames({
										[styles.progressContainer]: currentHotspot.id,
									}),
								}}
							/>
						</div>
					</div>
				)}
				{controlsInOptionsList.length > 0 && (
					<MobileViewControlOptions
						onOptionClick={onOptionClick}
						tooltipPosition={tooltipPosition}
						options={controlsInOptionsList}
						tooltipParent={tooltipParent}
						classes={{
							optionsWrapper: 'margin-l-auto',
						}}
					/>
				)}
				{/*<div*/}
				{/*	className={styles.controls}*/}
				{/*	style={*/}
				{/*		(isFullScreen || hideHeader) && !isTabView*/}
				{/*			? { width: `${rangeSiblingsWidth}px` }*/}
				{/*			: {}*/}
				{/*	}*/}
				{/*>*/}
				{/*</div>*/}
				{(width || 0) < MAX_TABLET_VIEW_WIDTH &&
					(isTitlePresent || isDescriptionPresent) && (
						<div className={styles.hotspotInfoContainer}>
							<div
								ref={hotspotInfoWrapperRef}
								className={classNames(styles.hotspotInfoWrapper, {
									[styles.singleChild]:
										((isTitlePresent && !isDescriptionPresent) ||
											(!isTitlePresent && isDescriptionPresent)) &&
										!hotspotInfoOverflowing,
								})}
							>
								{isTitlePresent && (
									<div
										className={classNames(styles.hotspotTitle, {
											[styles.withoutDescription]: !isDescriptionPresent,
										})}
										ref={hotspotTitleRef}
										dangerouslySetInnerHTML={{
											__html: sanitizeHtml(
												get(currentHotspot, 'values.title') ||
													get(currentHotspot, 'values.name') ||
													''
											),
										}}
									></div>
								)}
								{isDescriptionPresent && (
									<div
										className={classNames(
											styles.hotspotDescription,
											'instruction-description',
											{
												[styles.withoutTitle]: !isTitlePresent,
											}
										)}
										ref={hotspotDescriptionRef}
										dangerouslySetInnerHTML={{
											__html: sanitizeHtmlLinks(
												get(currentHotspot, 'values.description') || ''
											),
										}}
									></div>
								)}
							</div>
						</div>
					)}
			</div>
		</div>
	);
};

export default InteractiveModeControls;
